/** WhatsApp хранится в profiles.social_links как https://wa.me/<цифры>. */

export const WHATSAPP_ERROR = "Добавь код страны через +, например +7… или +380…";

export function isWhatsappLink(url: string): boolean {
  return /wa\.me|whatsapp/i.test(url);
}

/**
 * Номер → только цифры с кодом страны, иначе null. Принимаем:
 * «+» и 10–15 цифр; турецкий 0 5XX XXX XX XX / 5XX XXX XX XX (→ 90…); 90 и ещё 10 цифр;
 * ссылку wa.me/<цифры> (она всегда с кодом страны). Без «+» прочие номера отклоняем.
 */
export function normalizeWhatsapp(raw: string): string | null {
  let s = (raw ?? "").trim();
  const link = /^(https?:\/\/)?(www\.)?wa\.me\//i.test(s);
  s = s.replace(/^(https?:\/\/)?(www\.)?wa\.me\//i, "").replace(/[\s().-]/g, "");
  const plus = link || s.startsWith("+");
  s = s.replace(/^\+/, "");
  if (!/^\d+$/.test(s)) return null;
  if (plus) return s.length >= 10 && s.length <= 15 ? s : null;
  if (s.length === 11 && s.startsWith("05")) return `90${s.slice(1)}`;
  if (s.length === 10 && s.startsWith("5")) return `90${s}`;
  if (s.length === 12 && s.startsWith("90")) return s;
  return null;
}

/** Как покажем сохранённый номер: турецкий с пробелами, прочие «+» и цифры. */
export function formatWhatsapp(digits: string): string {
  const m = digits.match(/^90(\d{3})(\d{3})(\d{2})(\d{2})$/);
  return m ? `+90 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : `+${digits}`;
}

/** Похоже ли значение на телефон (для общего поля контакта). */
export function looksLikePhone(raw: string): boolean {
  return /^\+?[\d\s().-]{10,}$/.test((raw ?? "").trim());
}

export function whatsappLink(digits: string): string {
  return `https://wa.me/${digits}`;
}

/** Номер для предзаполнения поля из сохранённой ссылки wa.me. */
export function whatsappFromLinks(links: string[] | null | undefined): string {
  const l = (links ?? []).find((x) => !!x && isWhatsappLink(x));
  if (!l) return "";
  const m = l.match(/wa\.me\/\+?(\d+)/i) ?? l.match(/phone=\+?(\d+)/i);
  return m ? `+${m[1]}` : "";
}

/** Заменяет прежнюю WhatsApp-ссылку новой (или удаляет при null); прочие ссылки не трогает. */
export function withWhatsapp(links: string[] | null | undefined, digits: string | null): string[] {
  const rest = (links ?? []).filter((x) => !!x?.trim() && !isWhatsappLink(x));
  return digits ? [...rest, whatsappLink(digits)] : rest;
}

/** Instagram хранится в social_links как https://instagram.com/<ник>. */
export const INSTAGRAM_ERROR = "Не похоже на Instagram. Впиши ник, например @username";

export function isInstagramLink(url: string): boolean {
  return /instagram\.com/i.test(url);
}

/** @ник, ник, instagram.com/ник или полная ссылка → ник; иначе null. */
export function normalizeInstagram(raw: string): string | null {
  let s = (raw ?? "").trim();
  s = s.replace(/^(https?:\/\/)?(www\.|m\.)?instagram\.com\//i, "").replace(/^@/, "");
  s = s.split(/[/?#]/)[0];
  return /^[A-Za-z0-9._]{1,30}$/.test(s) ? s : null;
}

/** Ник для предзаполнения поля из сохранённой ссылки. */
export function instagramFromLinks(links: string[] | null | undefined): string {
  const l = (links ?? []).find((x) => !!x && isInstagramLink(x));
  return l ? (normalizeInstagram(l) ?? "") : "";
}

/** Заменяет прежнюю Instagram-ссылку новой (или удаляет при null); прочие ссылки не трогает. */
export function withInstagram(links: string[] | null | undefined, nick: string | null): string[] {
  const rest = (links ?? []).filter((x) => !!x?.trim() && !isInstagramLink(x));
  return nick ? [...rest, `https://instagram.com/${nick}`] : rest;
}
